import { useEffect, useRef, useState } from "react";
import { Outlet } from "react-router-dom";
import { browserStore, readRailHidden, writeRailHidden } from "../../lib/railPreference";
import { isMacPlatform, isSpotlightShortcut } from "../../lib/spotlight";
import { NavRail, RailToggleGlyph } from "./NavRail";
import { Spotlight } from "./Spotlight";

/**
 * The authenticated layout: a floating rail, scrolling content.
 *
 * The rail is `position: fixed` and inset from the viewport edge, and the content
 * is offset by its width plus both insets, rather than the two being flex
 * siblings, so a long table scrolls under a rail that never moves — and the
 * content column keeps a max width so a 32" monitor does not produce
 * 200-character table rows.
 *
 * On `lg` and up the rail can be hidden. It slides out and the content takes the
 * width back, with a small floating button left in its place; the choice is kept
 * per browser, because a wide audit table on a laptop is exactly when somebody
 * wants the extra 230px and they should not have to ask twice.
 *
 * Below `lg` the rail becomes a floating strip at the top of the document and the
 * offset is dropped, so a tablet gets a horizontally-scrolling nav row instead of
 * a table that overflows the viewport. Hiding is not offered there: the strip
 * scrolls away with the page already, so there is nothing to reclaim. This panel
 * is used on a laptop in a depot office and occasionally on a tablet beside a
 * charger; it is not a phone-first product, and pretending otherwise would cost
 * the table density that is the point of it.
 */
export function AppShell() {
  const [railHidden, setRailHidden] = useState(() => readRailHidden(browserStore()));
  const [spotlightOpen, setSpotlightOpen] = useState(false);

  // On the window, so the shortcut works from any page and from inside any field
  // — the chord types nothing there (and on a Mac, Ctrl+K is left to the field).
  useEffect(() => {
    const isMac = isMacPlatform();
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSpotlightShortcut(event, isMac)) return;
      event.preventDefault();
      setSpotlightOpen((open) => !open);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const hideButtonRef = useRef<HTMLButtonElement>(null);
  const showButtonRef = useRef<HTMLButtonElement>(null);
  // Set by a press, read once by the effect: focus follows the toggle, but a
  // page that loads with the rail already hidden does not have focus moved.
  const movedByPress = useRef(false);

  const toggleRail = () => {
    const next = !railHidden;
    movedByPress.current = true;
    setRailHidden(next);
    writeRailHidden(browserStore(), next);
  };

  // The button that was pressed is the one that just disappeared, so without this
  // a keyboard user's focus falls back to <body> on every toggle.
  useEffect(() => {
    if (!movedByPress.current) return;
    movedByPress.current = false;
    (railHidden ? showButtonRef : hideButtonRef).current?.focus();
  }, [railHidden]);

  return (
    <div className="min-h-dvh bg-paper">
      <NavRail
        isHidden={railHidden}
        onHide={toggleRail}
        hideButtonRef={hideButtonRef}
        onSearch={() => setSpotlightOpen(true)}
      />
      <Spotlight open={spotlightOpen} onOpenChange={setSpotlightOpen} />

      {/* Rendered only while the rail is away, so there is never a second
          control for the same thing in the tab order. */}
      {railHidden ? (
        <button
          ref={showButtonRef}
          type="button"
          onClick={toggleRail}
          aria-label="Show navigation"
          aria-controls="nav-rail"
          aria-expanded={false}
          className={[
            "fixed top-(--rail-inset) left-(--rail-inset) z-30 hidden lg:inline-flex",
            "size-9 items-center justify-center rounded-panel border border-rule bg-paper-raised shadow-float",
            "text-ink-muted transition-colors duration-fast ease-swiss hover:border-ink hover:text-ink active:translate-y-px",
          ].join(" ")}
        >
          <RailToggleGlyph direction="show" />
        </button>
      ) : null}

      <main
        className={[
          "transition-[padding] duration-stage ease-swiss",
          railHidden
            ? "lg:pl-[calc(var(--rail-inset)*2+2.25rem)]"
            : "lg:pl-[calc(var(--rail-width)+var(--rail-inset)*2)]",
        ].join(" ")}
      >
        <div className="mx-auto flex w-full max-w-(--content-max) flex-col gap-6 px-gutter pb-section">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
